import { defineComponent as _defineComponent } from 'vue'
import { resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "option-section mb-3"
}
const _hoisted_2 = { class: "d-flex align-items-center mb-3" }
const _hoisted_3 = { class: "body-strong mb-0" }
const _hoisted_4 = { class: "caption" }
const _hoisted_5 = {
  key: 3,
  class: "luggage-details"
}
const _hoisted_6 = { class: "luggage-detail-info" }
const _hoisted_7 = { class: "mb-0 caption" }
const _hoisted_8 = {
  key: 4,
  class: "default-service"
}
const _hoisted_9 = { class: "mb-0 caption" }
const _hoisted_10 = {
  key: 0,
  class: "caption-strong"
}

import { computed } from 'vue'
  import { useStore } from 'vuex'
  import { useI18n } from 'vue-i18n'
  import { Service } from '@/services/CatalogService.interface'
  import { FamilyIds } from '@/assets/families'
  import {ServiceType} from "@/config"
  import FlightDetails from './FlightDetails.vue'
  import TrainDetails from './TrainDetails.vue'
  import TransferDetails from './TransferDetails.vue'
  import { BookingSummary } from '@/services/BookingService.interface'

  import { useSectionVisibilty } from '@/composables/useSectionVisibilty'
  import {
    dedupeTransferOptionsForDisplay,
    resolveTripInfoForOption,
    transferTypeFromOptionName,
  } from '@/utils/transferTripInformation'
  import {
    getLuggageCoverage,
    getLuggageDetails,
    isLuggageLeafSelected,
    resolveLuggageTripInfo,
    type LuggageOptionLike,
  } from '@/utils/luggageTripInformation'

  // Define the props with types
  
export default /*@__PURE__*/_defineComponent({
  __name: 'ReservationOptionSection',
  props: {
    options: {},
    sectionTitle: {},
    iconComponent: {}
  },
  setup(__props: any) {

  const props = __props

  const store = useStore()
  const { t } = useI18n()

  const toLuggageOption = (item: Partial<Service>): LuggageOptionLike => ({
    optionId: item.optionId,
    id: item.id,
    nameCommercial: item.nameCommercial,
    sellingPrice: item.sellingPrice,
    quantity: item.quantity,
    familyId: item.familyId,
    tripInformation: item.tripInformation,
  })

  const luggageSiblings = computed(() =>
    props.options
      .filter(option => option.familyId === FamilyIds.LUGGAGE)
      .map(toLuggageOption)
  )

  const luggageDisplayLabel = (item: Partial<Service>): string => {
    const matched = resolveLuggageTripInfo(toLuggageOption(item), luggageSiblings.value)
    const details = getLuggageDetails(matched)
    const description =
      (typeof details?.description === 'string' && details.description) ||
      item.nameCommercial ||
      t('reservations.luggage')
    const weight = details?.weight
    const coverage = getLuggageCoverage(details)
    const coverageLabel =
      coverage === 'both-ways'
        ? t('reservations.luggageBothWays')
        : coverage === 'outbound-only'
          ? t('reservations.luggageOutboundOnly')
          : coverage === 'inbound-only'
            ? t('reservations.luggageInboundOnly')
            : ''
    if (weight != null && weight !== '') {
      return coverageLabel
        ? `${description}: ${weight}kg (${coverageLabel})`
        : `${description}: ${weight}kg`
    }
    return item.nameCommercial || String(description)
  }

  const transferTypeLabels = computed(() => ({
    bus: t('reservations.bus'),
    taxi: t('reservations.taxi'),
    funicular: t('reservations.funicular'),
  }))

  const resolvedTransferTrip = (item: Partial<Service>) =>
    resolveTripInfoForOption(
      {
        prestId: item.id,
        familyId: item.familyId,
        name: item.nameCommercial,
      },
      item.tripInformation
    )

  const getResolvedTransferType = (item: Partial<Service>) => {
    const resolved = resolvedTransferTrip(item)
    if (resolved.length) {
      return getTransferType(resolved)
    }
    return (
      transferTypeFromOptionName(item.nameCommercial, transferTypeLabels.value) ??
      t('reservations.transfer')
    )
  }

  // Get dynamic section title with transfer type
  const dynamicSectionTitle = computed(() => {
    // Change title for transfer sections based on transfer type
    if (props.sectionTitle.toLowerCase().includes('transfer')) {
      const transferOption = filteredOptions.value.find(option => option.familyId === FamilyIds.TRANSFER)
      if (transferOption) {
        const transferType = getResolvedTransferType(transferOption)
        if (transferType && transferType !== 'Transfer') {
          return transferType
        }
      }
    }
    return props.sectionTitle
  })

  // Get title parts for all sections (bold and regular text)
  const titleParts = computed(() => {
    // Handle transfer sections
    if (props.sectionTitle.toLowerCase().includes('transfer')) {
      const transferOption = filteredOptions.value.find(option => option.familyId === FamilyIds.TRANSFER)
      if (transferOption) {
        const transferType = getResolvedTransferType(transferOption)
        if (transferType && transferType !== t('reservations.transfer')) {
          // Check if the transfer type contains "(Service Hours 24/7)"
          if (transferType.includes(`(${t('reservations.serviceHours24')})`)) {
            const baseType = transferType.replace(` (${t('reservations.serviceHours24')})`, '')
            return {
              bold: baseType,
              regular: ` (${t('reservations.serviceHours24')})`
            }
          }
          return {
            bold: transferType,
            regular: ''
          }
        }
      }
    }
    
    // Handle train sections
    if (props.sectionTitle.toLowerCase().includes('train')) {
      // For now, just use the translated train title
      // Could be extended later to show specific train types if needed
      return {
        bold: t('reservations.train'),
        regular: ''
      }
    }
    
    // Handle flight sections
    if (props.sectionTitle.toLowerCase().includes('flight')) {
      // For now, just use the translated flight title
      // Could be extended later to show specific flight types if needed
      return {
        bold: t('reservations.flight'),
        regular: ''
      }
    }
    
    // Default case - use the provided section title (which should already be translated)
    return {
      bold: props.sectionTitle,
      regular: ''
    }
  })
  
  const selectedBooking = computed<BookingSummary | null>(
    () => store.getters['booking/selectedBooking']
  )

  const {showAccommodationOption} = useSectionVisibilty(selectedBooking.value?.liheId || 0, selectedBooking.value?.liheName, selectedBooking.value?.packageCode)

  // Filter out services with 'Sans' in nameCommercial and unselected luggage
  const filteredOptions = computed(() => {
    const filtered = props.options.filter(option => {
      console.log("option: ",)
      if(option.serviceType === ServiceType.ACCOMMODATION && !showAccommodationOption.value) {
        return false
      } 

      // Filter out services with 'Sans' in nameCommercial
      if (!option.nameCommercial || option.nameCommercial.toLowerCase().includes('sans')) {
        return false
      }
      
      if (option.familyId === FamilyIds.LUGGAGE && option.tripInformation) {
        const matched = resolveLuggageTripInfo(
          toLuggageOption(option),
          luggageSiblings.value
        )
        if (matched && !isLuggageLeafSelected(matched)) {
          return false
        }
      }
      
      return true
    })
    return dedupeTransferOptionsForDisplay(filtered)
  })

  // Simple validation functions for child components
  const hasFlightDetails = (tripInformation: any) => {
    console.log('hasFlightDetails called with:', tripInformation)
    
    if (Array.isArray(tripInformation) && tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      const flightDetail = tripInformation[0].details || tripInformation[0].tripInformation
      console.log('Flight detail from array:', flightDetail)
      const hasDetails = flightDetail && (flightDetail.outboundTrip || flightDetail.inboundTrip || flightDetail.class)
      console.log('Has flight details:', hasDetails)
      return hasDetails
    }
    
    console.log('Flight detail from direct object:', tripInformation)
    const hasDetails = tripInformation && (tripInformation.outboundTrip || tripInformation.inboundTrip || tripInformation.class)
    console.log('Has flight details:', hasDetails)
    return hasDetails
  }

  const hasTrainDetails = (tripInformation: any) => {
    if (Array.isArray(tripInformation) && tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      const trainDetail = tripInformation[0].details || tripInformation[0].tripInformation
      return trainDetail && (trainDetail.outboundTrip || trainDetail.inboundTrip || trainDetail.class)
    }
    return tripInformation && (tripInformation.outboundTrip || tripInformation.inboundTrip || tripInformation.class)
  }

  const hasTransferDetails = (tripInformation: any) => {
    if (Array.isArray(tripInformation) && tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      const transferDetail = tripInformation[0].details || tripInformation[0].tripInformation
      // Check for taxi transfer structure (has transferType and time ranges)
      if (transferDetail && transferDetail.transferType && 
          (transferDetail.outboundHours || transferDetail.inboundHours)) {
        return true
      }
      // Check for bus transfer structure with references
      if (transferDetail && (transferDetail.transferReturnRef || transferDetail.transferDepartureRef)) {
        return true
      }
      // Fallback to original structure
      return transferDetail && (transferDetail.departInfo || transferDetail.arriveeInfo)
    }
    // Check direct object for taxi transfers
    if (tripInformation && tripInformation.transferType && (tripInformation.outboundHours || tripInformation.inboundHours)) {
      return true
    }
    // Check direct object for bus transfers
    if (tripInformation && (tripInformation.transferReturnRef || tripInformation.transferDepartureRef)) {
      return true
    }
    return tripInformation && (tripInformation.departInfo || tripInformation.arriveeInfo)
  }

  // Helper function to check if service hours indicate 24/7
  const is24HourService = (hours: any) => {
    if (!hours) return false
    if (Array.isArray(hours)) return false
    
    // Check if the time indicates 24/7 service (00:01 or 23:59)
    return (hours.from === '00:01' && hours.to === '23:59') || 
           (hours.from === '23:59' && hours.to === '00:01')
  }

  // Get transfer type from tripInformation
  const getTransferType = (tripInformation: any) => {
    let transferType = t('reservations.transfer')
    let is24Hours = false
    
    if (Array.isArray(tripInformation) && tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      const transferDetail = tripInformation[0].details || tripInformation[0].tripInformation
      if (transferDetail?.transferType === 'TAXI') {
        transferType = t('reservations.taxi')
        // Check for 24/7 service hours
        is24Hours = is24HourService(transferDetail.outboundHours) || is24HourService(transferDetail.inboundHours)
      } else if (transferDetail?.transferType === 'BUS') {
        transferType = t('reservations.bus')
      } else if (transferDetail?.transferType === 'FUNICULAR') {
        transferType = t('reservations.funicular')
      } else if (transferDetail?.outboundHours && !Array.isArray(transferDetail.outboundHours) && (transferDetail.outboundHours as any).provider === 'FUNICULAR') {
        transferType = t('reservations.funicular')
      } else if (transferDetail?.inboundHours && !Array.isArray(transferDetail.inboundHours) && (transferDetail.inboundHours as any).provider === 'FUNICULAR') {
        transferType = t('reservations.funicular')
      } else if (transferDetail?.outboundHours && !Array.isArray(transferDetail.outboundHours) && 'provider' in transferDetail.outboundHours) {
        transferType = transferDetail.outboundHours.provider
      }
    } else {
      if (tripInformation?.transferType === 'TAXI') {
        transferType = t('reservations.taxi')
        // Check for 24/7 service hours
        is24Hours = is24HourService(tripInformation.outboundHours) || is24HourService(tripInformation.inboundHours)
      } else if (tripInformation?.transferType === 'BUS') {
        transferType = t('reservations.bus')
      } else if (tripInformation?.transferType === 'FUNICULAR') {
        transferType = t('reservations.funicular')
      } else if (tripInformation?.outboundHours && !Array.isArray(tripInformation.outboundHours) && (tripInformation.outboundHours as any).provider === 'FUNICULAR') {
        transferType = t('reservations.funicular')
      } else if (tripInformation?.inboundHours && !Array.isArray(tripInformation.inboundHours) && (tripInformation.inboundHours as any).provider === 'FUNICULAR') {
        transferType = t('reservations.funicular')
      } else if (tripInformation?.outboundHours && !Array.isArray(tripInformation.outboundHours) && 'provider' in tripInformation.outboundHours) {
        transferType = tripInformation.outboundHours.provider
      }
    }
    
    // Return transfer type with 24/7 indicator if applicable
    return is24Hours ? `${transferType} (${t('reservations.serviceHours24')})` : transferType
  }

return (_ctx: any,_cache: any) => {
  return (filteredOptions.value.length)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.iconComponent), { class: "me-2" })),
          _createElementVNode("h5", _hoisted_3, [
            _createTextVNode(_toDisplayString(titleParts.value.bold), 1),
            _createElementVNode("span", _hoisted_4, _toDisplayString(titleParts.value.regular), 1)
          ])
        ]),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOptions.value, (item, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: "mb-2"
          }, [
            (item.familyId === _unref(FamilyIds).FLIGHT && item.tripInformation && hasFlightDetails(item.tripInformation))
              ? (_openBlock(), _createBlock(FlightDetails, {
                  key: 0,
                  tripInformation: item.tripInformation
                }, null, 8, ["tripInformation"]))
              : ((item.familyId === _unref(FamilyIds).TRAIN || item.familyId === _unref(FamilyIds).TRAIN_ALTERNATIVE) && item.tripInformation && hasTrainDetails(item.tripInformation))
                ? (_openBlock(), _createBlock(TrainDetails, {
                    key: 1,
                    tripInformation: item.tripInformation
                  }, null, 8, ["tripInformation"]))
                : (item.familyId === _unref(FamilyIds).TRANSFER && resolvedTransferTrip(item).length && hasTransferDetails(resolvedTransferTrip(item)))
                  ? (_openBlock(), _createBlock(TransferDetails, {
                      key: 2,
                      tripInformation: resolvedTransferTrip(item)
                    }, null, 8, ["tripInformation"]))
                  : (item.familyId === _unref(FamilyIds).LUGGAGE && item.tripInformation && Array.isArray(item.tripInformation) && item.tripInformation.length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                        _createElementVNode("div", _hoisted_6, [
                          _createElementVNode("p", _hoisted_7, " × " + _toDisplayString(item.quantity || 1) + " " + _toDisplayString(luggageDisplayLabel(item)), 1)
                        ])
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createElementVNode("p", _hoisted_9, [
                          _createTextVNode(_toDisplayString(item.nameCommercial) + " ", 1),
                          (item.quantity && item.quantity > 1)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_10, "x" + _toDisplayString(item.quantity), 1))
                            : _createCommentVNode("", true)
                        ])
                      ]))
          ]))
        }), 128))
      ]))
    : _createCommentVNode("", true)
}
}

})